<script setup>
import { computed, inject, watchEffect } from "vue";
import VSelect from "vue-select";
import ButtonWithSpinner from "@/components/forms/SharedComponents/ButtonWithSpinner.vue";
import { entityTypes, sessionStatus } from "@/components/program-manager/sessions/constants.js";
import { useAllowedUniversities } from "@/components/RegistrationProcess/composables/useAllowedUniversities.ts";
import { useLearnerTypes } from "@/components/RegistrationProcess/composables/useLearnerTypes.js";
import ArrowDown from "@/components/shared/icons/ArrowDown.vue";
import ArrowRight from "@/components/shared/icons/ArrowRight.vue";
import SkeletonLoader from "@/components/shared/loaders/BaseSkeletonLoader.vue";
import SchoolSearchSelect from "@/components/shared/SchoolSearchSelect/index.vue";
import ExternalLink from "@/components/svg-icons/ExternalLink.vue";
import { INVALID_SELECTION, requiredSchool } from "@/constants";
import entitiesService from "@/services/entities.js";
import { durationLabel } from "@/util/date";

const {
  program,
  selectedSessionId,
  selectedSession,
  loadedSessions,
  setLoadedUniversities,
  homeInstitutionId,
  learnerTypeId,
  profileData,
  hasValidLearnerType,
  hasValidHomeInstitution,
  programPageId,
} = inject("program");

const { goToNextStep, currentStep } = inject("steps");

const { updateProfileColleges } = inject("updates");

const isActionButtonEnabled = computed(() => currentStep.value.isComplete());

const handleActionButtonClick = async () => {
  if (isActionButtonEnabled.value && !hasInvalidSelections.value) {
    await updateProfileColleges();
    goToNextStep();
    scrollUp();
  }
};

const getTravelDetails = (session) => {
  const travelDetails = session?.session_travel_details ?? [];

  const sortedAsc = travelDetails.sort((a, b) => Number(new Date(a.arrival_date)) - Number(new Date(b.arrival_date)));
  const arrivalDetails = sortedAsc.length ? sortedAsc[0] : {};

  const sortedDesc = travelDetails.sort(
    (a, b) => Number(new Date(b.departure_date)) - Number(new Date(a.departure_date)),
  );
  const departureDetails = sortedDesc.length ? sortedDesc[0] : {};

  return {
    startDate: arrivalDetails?.arrival_date ?? "",
    endDate: departureDetails?.departure_date ?? "",
  };
};

const showOtherSessionsLink = computed(() => {
  const openEnrollmentSessions = loadedSessions.value.filter(
    (session) => session?.status_id === sessionStatus.openEnrollment,
  );
  return openEnrollmentSessions.length > 1 && programPageId.value;
});

const { filteredLearnerTypes, currentLearnerTypeIsValid, invalidLearnerTypeFromProfile } = useLearnerTypes(
  profileData,
  selectedSession,
  learnerTypeId,
);

const selectedLearnerType = computed({
  get: () => {
    if (!learnerTypeId.value) return undefined;
    return filteredLearnerTypes.value.find((type) => type.id === learnerTypeId.value);
  },
  set: (newType) => {
    learnerTypeId.value = newType?.id;
  },
});

const selectedHomeInstitution = computed({
  get: () => {
    if (!homeInstitutionId.value) return undefined;
    return allowedUniversities.value.find((uni) => uni.id === homeInstitutionId.value);
  },
  set: (newInstitution) => {
    homeInstitutionId.value = newInstitution?.id;
  },
});

const {
  currentCollegeIsValid,
  invalidCollegeFromProfile,
  universities: allowedUniversities,
} = useAllowedUniversities(
  profileData,
  selectedSession,
  computed(() => program.loadedUniversities ?? []),
  selectedHomeInstitution,
);

const invalidHomeInstitutionSelected = computed(
  () =>
    requiredSchool.includes(learnerTypeId.value) && invalidCollegeFromProfile.value && !currentCollegeIsValid.value,
);

const hasInvalidSelections = computed(
  () =>
    (invalidLearnerTypeFromProfile.value && !currentLearnerTypeIsValid.value) ||
    (requiredSchool.includes(learnerTypeId.value) && invalidCollegeFromProfile.value && !currentCollegeIsValid.value),
);

watchEffect(() => {
  hasValidLearnerType.value = !!selectedLearnerType.value && currentLearnerTypeIsValid.value;
  hasValidHomeInstitution.value =
    !requiredSchool.includes(learnerTypeId.value) || (!!selectedHomeInstitution.value && currentCollegeIsValid.value);
});

const isFacultyLed = computed(() => {
  return selectedSession.value ? selectedSession.value?.session_types?.some((type) => type.id === 4) : false;
});

const selectedSessionLabel = computed(() => {
  const { startDate, endDate } = getTravelDetails(selectedSession.value);
  return durationLabel({ startDate, endDate });
});

const search = async ({ search, loading }) => {
  if (search.length === 0) {
    // Reset to just the currently-selected school so it stays selectable without an extra fetch.
    setLoadedUniversities(program.school ? [program.school] : []);
    return;
  }
  // Session whitelists its institutions
  // vue-select filters the whitelist client-side, no fetch needed.
  if ((selectedSession.value?.allowed_participants?.length ?? 0) > 0) {
    return;
  }
  loading(true);
  entitiesService
    .getEntities({
      q: search,
      extraParams: {
        account_types: [entityTypes.home_institution],
        field: "name",
        order: "ASC",
      },
    })
    .then(({ data }) => {
      const results = data?.data?.items ?? [];
      // Keep the currently-selected school in the options so it stays displayed while the user explores other matches.
      const selected = program.school;
      setLoadedUniversities(
        selected && !results.some((school) => school.id === selected.id) ? [selected, ...results] : results,
      );
    })
    .catch(() => {
      setLoadedUniversities(program.school ? [program.school] : []);
    })
    .finally(() => {
      loading(false);
    });
};

const checkIfDeviceIsMobile = () => {
  const screenWidth = window.innerWidth || document.documentElement.clientWidth;
  return screenWidth < 900;
};

const scrollUp = () => {
  if (checkIfDeviceIsMobile()) {
    const element = document.getElementById("process-steps");
    if (element) {
      element.scrollIntoView({
        behavior: "auto",
        block: "start",
      });
    }
  } else {
    window.scrollTo({
      top: 0,
      behavior: "auto",
    });
  }
};
</script>
<template>
  <div class="px-6 py-6 text-lg md:px-24 md:py-12 text-indigo-base">
    <h1
      v-if="!isFacultyLed"
      data-testid="personalize-header"
      class="font-bold font-montserrat md:text-configurator-h1 md:uppercase"
    >
      Personalize Your Adventure
    </h1>
    <h2 class="mt-4 mb-6 ml-6 md:mt-0 md:ml-0 font-montserrat md:text-xl">
      Before we get started, please confirm the information below.
    </h2>
    <div class="p-2">
      <div class="grid grid-cols-1 gap-4 w-full md:grid-cols-2 md:gap-x-6 md:grid-rows-[auto_auto] md:items-start">
        <div class="md:grid md:grid-rows-subgrid md:row-span-2 md:col-start-1">
          <div>
            <label class="block text-lg md:font-semibold mt-2 mb-1">
              What best describes you?
            </label>
            <p class="block mb-2 text-sm invisible" aria-hidden="true">
              Pick the option that matches your situation — e.g. select "College Student" if currently enrolled.
            </p>
            <SkeletonLoader v-if="filteredLearnerTypes.length === 0" rounded />
            <template v-else>
              <v-select
                id="learnerType"
                v-model="selectedLearnerType"
                data-testid="learner-type-input-box"
                class="v-select"
                :disabled="!selectedSession"
                :options="filteredLearnerTypes"
                :clearable="false"
                :get-option-label="(option) => option.value"
                append-icon="ArrowDown"
              >
                <template #open-indicator="{ attributes }">
                  <ArrowDown :attributes="attributes" />
                </template>

                <template #option="option">
                  <div>
                    <div
                      :data-testid="`${option.value.replace(/\s+/g, '-')}-option`"
                      class="inline-block align-top whitespace-normal break-words overflow-wrap-anywhere"
                    >
                      <p style="margin: 0;">
                        {{ option.value }}
                      </p>
                    </div>
                  </div>
                </template>
              </v-select>
            </template>
          </div>

          <p
            v-if="invalidLearnerTypeFromProfile && !currentLearnerTypeIsValid"
            class="error text-error-900 text-sm"
          >
            {{ INVALID_SELECTION }}
          </p>
        </div>

        <div
          v-if="requiredSchool.includes(learnerTypeId)"
          data-testid="uni-search-instruction"
          class="md:grid md:grid-rows-subgrid md:row-span-2 md:col-start-2"
        >
          <SchoolSearchSelect
            id="homeInstitution"
            v-model="selectedHomeInstitution"
            test-id="college-option-input-box"
            variant="heading"
            label="What is the name of your college/university?"
            description='Start typing your school name to search — e.g. type "Florida" for University of Central Florida.'
            placeholder="Type to search your university..."
            :options="allowedUniversities"
            :search-debounce-ms="500"
            @search="search"
          />
          <p
            v-if="invalidHomeInstitutionSelected"
            class="error text-error-900 text-sm"
            data-testid="home-institution-error"
          >
            {{ INVALID_SELECTION }}
          </p>
        </div>

      </div>
      <hr class="block my-8 w-full border border-light-gray" />
      <div class="p-2 md:w-3/6">
        <label class="block my-2 text-lg md:font-semibold">
          Program Session:
        </label>
        <p class="font-monserrat text-15p py-3 font-medium">
          {{ selectedSessionLabel }}
        </p>
        <router-link
          v-if="showOtherSessionsLink"
          data-testid="other-sessions-link"
          :to="{
            name: 'program-detail-view',
            params: { programPageId: programPageId },
            query: { 'session-id': selectedSessionId },
            hash: '#dates',
          }"
          target="_blank"
          :class="`font-semibold text-sm ${secondaryColorClass} inline-flex items-center gap-2`"
        >
          <span>Explore other available session dates</span>
          <ExternalLink class="w-3 h-3"/>
        </router-link>
      </div>
    </div>
    <div
      class="flex justify-center items-center px-8 py-4 mx-auto my-8 align-middle font-montserrat md:ml-auto md:-mr-20"
    >
      <ButtonWithSpinner
        id="getStartedButton"
        ref="getStartedButton"
        data-testid="get-started-button"
        type="submit"
        variant="secondary"
        variant-type="block"
        button-height="min-h-[58px] md:min-h-[60px]"
        :grey-disabled-class="true"
        :disabled="!isActionButtonEnabled || hasInvalidSelections"
        @click="handleActionButtonClick"
      >
        <div class="flex justify-center items-center text-lg md:text-base md:font-bold">
          <span class="pr-3 uppercase">
            Get started
          </span>
          <ArrowRight custom-class="w-4 h-4" />
        </div>
      </ButtonWithSpinner>
    </div>
  </div>
</template>
<style scoped>
.v-select {
  --vs-border-radius: 0;
}

.v-select > .vs__dropdown-toggle {
  border-radius: 0 !important;
  border-color: black !important;
  color: #1e264c !important;
}
</style>
